import { Box, Typography, IconButton, Paper, Chip, Collapse, Tooltip } from "@mui/material";
import { Close as CloseIcon, AccessTime, ExpandMore, ExpandLess } from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { useState } from "react";
import { AvailableSlot, FutureAvailableSlot, FindAvailableSlotsResult, ServiceAvailability } from "./types";
import { WEEK_DAY_FULL_LABELS } from "./constants";

type AvailableSlotsDrawerProps = {
    open: boolean;
    onClose: () => void;
    results: FindAvailableSlotsResult | null;
    includePsy: boolean;
    includeNfb: boolean;
};

type WeekdayGroupProps = {
    weekDay: number;
    slots: Array<AvailableSlot>;
    showAvailableFrom?: boolean;
};

const WeekdayGroup = ({ weekDay, slots, showAvailableFrom = false }: WeekdayGroupProps) => {
    const label = WEEK_DAY_FULL_LABELS[weekDay];

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: "bold", mb: 1 }}>
                {label}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {slots.map((slot, idx) => {
                    const futureSlot = slot as FutureAvailableSlot;
                    const hasAvailableFrom = showAvailableFrom && futureSlot.availableFromDate;
                    const tooltipDate = hasAvailableFrom
                        ? format(parseISO(futureSlot.availableFromDate), "d MMM yyyy", { locale: pt })
                        : format(parseISO(slot.firstAvailableDate), "EEE, d MMMM", { locale: pt });
                    const tooltipText = hasAvailableFrom ? `A partir de ${tooltipDate}` : tooltipDate;

                    return (
                        <Tooltip key={`${slot.weekDay}-${slot.time}-${idx}`} title={tooltipText} arrow>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <Chip
                                    icon={<AccessTime sx={{ fontSize: 14 }} />}
                                    label={slot.time}
                                    size="small"
                                    variant="outlined"
                                    sx={{ color: "primary.text" }}
                                />
                                {hasAvailableFrom && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px", mt: 0.25 }}>
                                        {format(parseISO(futureSlot.availableFromDate), "d/MM", { locale: pt })}
                                    </Typography>
                                )}
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
};

type SlotsSectionProps = {
    title: string;
    slots: Array<AvailableSlot>;
    defaultExpanded?: boolean;
    showAvailableFrom?: boolean;
};

const SlotsSection = ({ title, slots, defaultExpanded = true, showAvailableFrom = false }: SlotsSectionProps) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const groupedByWeekday: Record<number, Array<AvailableSlot>> = {};
    slots.forEach((slot) => {
        if (!groupedByWeekday[slot.weekDay]) {
            groupedByWeekday[slot.weekDay] = [];
        }
        groupedByWeekday[slot.weekDay].push(slot);
    });

    const weekdays = Object.keys(groupedByWeekday)
        .map(Number)
        .sort((a, b) => a - b);

    return (
        <Box sx={{ mb: 2 }}>
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Typography variant="body1" fontWeight="bold" color="text.primary">
                    {title}
                    <Chip
                        label={slots.length}
                        size="small"
                        color={slots.length > 0 ? "secondary" : "default"}
                        sx={{ ml: 1, height: 20, fontSize: "11px" }}
                    />
                </Typography>
                {expanded ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ pt: 2 }}>
                    {slots.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                            Nenhum horário disponível
                        </Typography>
                    ) : (
                        weekdays.map((weekDay) => (
                            <WeekdayGroup
                                key={weekDay}
                                weekDay={weekDay}
                                slots={groupedByWeekday[weekDay]}
                                showAvailableFrom={showAvailableFrom}
                            />
                        ))
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};

type ColumnProps = {
    title: string;
    subtitle?: string;
    weeklySlots: Array<AvailableSlot>;
    biweeklySlots: Array<AvailableSlot>;
    showAvailableFrom?: boolean;
    showBiweekly?: boolean;
};

const Column = ({ title, subtitle, weeklySlots, biweeklySlots, showAvailableFrom = false, showBiweekly = true }: ColumnProps) => {
    const totalSlots = weeklySlots.length + (showBiweekly ? biweeklySlots.length : 0);
    const countText = totalSlots === 0 ? "Nenhum horário" : `${totalSlots} opção(ões)`;
    const subtitleText = subtitle ? `${countText} · ${subtitle}` : countText;

    return (
        <Box sx={{ flex: 1, minWidth: 0, px: 1 }}>
            <Box sx={{ mb: 2, pb: 1, borderBottom: "2px solid", borderColor: "primary.main", height: 60 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "11px" }}>
                    {subtitleText}
                </Typography>
            </Box>
            <SlotsSection title="Semanal" slots={weeklySlots} defaultExpanded={true} showAvailableFrom={showAvailableFrom} />
            {showBiweekly && (
                <SlotsSection
                    title="Quinzenal"
                    slots={biweeklySlots}
                    defaultExpanded={weeklySlots.length === 0}
                    showAvailableFrom={showAvailableFrom}
                />
            )}
        </Box>
    );
};

const serviceAvailabilityCount = (availability: ServiceAvailability) => {
    const count =
        availability.weekly.length +
        availability.biweeklyA.length +
        availability.biweeklyB.length +
        availability.futureWeekly.length +
        availability.futureBiweeklyA.length +
        availability.futureBiweeklyB.length;
    return count;
};

type ServiceSectionProps = {
    label: string;
    availability: ServiceAvailability;
    showBiweekly: boolean;
};

const ServiceAvailabilitySection = ({ label, availability, showBiweekly }: ServiceSectionProps) => {
    const biweeklyNowSlots = [...availability.biweeklyA, ...availability.biweeklyB];
    const biweeklyFutureSlots = [...availability.futureBiweeklyA, ...availability.futureBiweeklyB];

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ backgroundColor: "secondary.main", color: "#fff", px: 2, py: 0.75, borderRadius: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                    {label}
                </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
                <Column
                    title="Disponível Agora"
                    weeklySlots={availability.weekly}
                    biweeklySlots={biweeklyNowSlots}
                    showBiweekly={showBiweekly}
                />
                <Box sx={{ width: "1px", backgroundColor: "divider", alignSelf: "stretch" }} />
                <Column
                    title="Disponível em Breve"
                    subtitle="livres após reservas terminarem"
                    weeklySlots={availability.futureWeekly}
                    biweeklySlots={biweeklyFutureSlots}
                    showAvailableFrom={true}
                    showBiweekly={showBiweekly}
                />
            </Box>
        </Box>
    );
};

export const AvailableSlotsDrawer = ({ open, onClose, results, includePsy, includeNfb }: AvailableSlotsDrawerProps) => {
    if (!results || !open) return null;

    const psyCount = includePsy ? serviceAvailabilityCount(results.psy) : 0;
    const nfbCount = includeNfb ? serviceAvailabilityCount(results.nfb) : 0;
    const totalFound = psyCount + nfbCount;

    return (
        <Paper
            elevation={8}
            sx={{
                position: "fixed",
                bottom: 24,
                right: 24,
                width: 700,
                maxHeight: "75vh",
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                overflow: "hidden",
                zIndex: 1300,
            }}
        >
            <Box
                sx={{
                    backgroundColor: "primary.main",
                    color: "#fff",
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight="bold">
                        Horários Disponíveis
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {totalFound === 0 ? "Nenhum horário encontrado" : `${totalFound} opção(ões) encontrada(s)`}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
                {includePsy && <ServiceAvailabilitySection label="Psicologia" availability={results.psy} showBiweekly={true} />}
                {includeNfb && <ServiceAvailabilitySection label="Neurofeedback" availability={results.nfb} showBiweekly={false} />}
            </Box>
        </Paper>
    );
};
